import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { StoreErrorHandler, getErrorMessage } from '@zero/shared/util-global-state';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { LombardInfrastructure } from '../infrastructure/lombard.infrastructure';
import { lombardActions, lombardApiActions } from './lombard.actions';

@Injectable({ providedIn: 'root' })
export class LombardEffects {
  #actions$ = inject(Actions);
  #infra = inject(LombardInfrastructure);
  #storeErrorHandler = inject(StoreErrorHandler);

  public loadLombard$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(lombardActions.loadLombard),
      switchMap(() =>
        this.#infra.getLombard().pipe(
          map((lombard) => lombardApiActions.loadLombardSuccess({ lombard })),
          catchError((error) => of(lombardApiActions.loadLombardFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public loadConfirmationData$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(lombardActions.loadConfirmationData),
      switchMap(() =>
        this.#infra.getConfirmationData().pipe(
          map((confirmationData) => lombardApiActions.loadConfirmationDataSuccess({ confirmationData })),
          catchError((error) => of(lombardApiActions.loadConfirmationDataFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public loadFAQs$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(lombardActions.loadFAQs),
      switchMap(() =>
        this.#infra.getFAQs().pipe(
          map((faqs) => lombardApiActions.loadFAQsSuccess({ faqs })),
          catchError((error) => of(lombardApiActions.loadFAQsFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public applyForCredit$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(lombardActions.applyForCredit),
      switchMap(({ credit }) =>
        this.#infra.apply(credit).pipe(
          map((lombard) => lombardApiActions.applyForCreditSuccess({ lombard })),
          catchError((error) => of(lombardApiActions.applyForCreditFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public dismissTeaser$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(lombardActions.dismissTeaser),
      switchMap(() =>
        this.#infra.dismissTeaser().pipe(
          map(() => lombardApiActions.dismissTeaserSuccess()),
          catchError((error) => of(lombardApiActions.dismissTeaserFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public dismissOffer$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(lombardActions.dismissOffer),
      switchMap(() =>
        this.#infra.dismissOffer().pipe(
          map((lombard) => lombardApiActions.dismissOfferSuccess({ lombard })),
          catchError((error) => of(lombardApiActions.dismissOfferFailure({ error: getErrorMessage(error) }))),
        ),
      ),
    );
  });

  public handleBackendError$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(
          lombardApiActions.applyForCreditFailure,
          lombardApiActions.loadFAQsFailure,
          lombardApiActions.loadLombardFailure,
          lombardApiActions.loadConfirmationDataFailure,
          lombardApiActions.dismissTeaserFailure,
          lombardApiActions.dismissOfferFailure,
        ),
        tap(({ error }) => this.#storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );
}
